import UnlockContact from '../components/Credits';


import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import Navbar from './Navbar';
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';

const ChevronLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FunnelIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <polygon points="4 4 20 4 14 12.5 14 19 10 21 10 12.5 4 4" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ResumeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

const BriefcaseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const MatchBadge = ({ percent }) => {
  if (percent == null) return null;
  const color =
    percent >= 80 ? 'bg-green-100 text-green-700'
    : percent >= 50 ? 'bg-blue-100 text-[#2563EB]'
    : 'bg-orange-100 text-orange-700';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full tabular-nums shrink-0 ${color}`}>
      ✦ {Math.round(percent)}% match
    </span>
  );
};

const MatchBreakdown = ({ breakdown, highMatch }) => {
  if (!breakdown || breakdown.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2 mb-1">
      <span className="text-xs font-bold text-[#2563EB] mr-0.5">✦ Matching:</span>
      {breakdown.map((b) => (
        <span
          key={b.key}
          className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${
            b.matched ? 'border-[#2563EB]/40 text-[#2563EB] bg-[#EFF6FF]' : 'border-gray-200 text-gray-400 bg-gray-50'
          }`}
        >
          {b.matched ? '✓' : '✕'} {b.label}
        </span>
      ))}
    </div>
  );
};

// ₹1,80,000 -> "1.8 Lakh". Falls back to a plain rupee figure under 1 lakh.
const formatSalary = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  if (num >= 100000) return `${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 1)} Lakh`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const formatExperience = (years) => {
  if (years === null || years === undefined || years === '') return null;
  const num = Number(years);
  if (Number.isNaN(num)) return null;
  if (num < 1) return `${Math.round(num * 12)} mos`;
  return `${num} yr${num === 1 ? '' : 's'}`;
};

const formatActiveDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }).replace(/ /g, ' ');
};

const emptyFilters = {
  skills: [],
  location: '',
  max_salary: '',
  min_experience: '',
  max_experience: '',
  gender: '',
  education: '',
  languages: [],
};

// Turns the backend's applied_filters shape (arrays for skills/languages,
// numbers for salary/experience) into the plain strings the filter inputs
// edit, so the two stay in sync without re-deriving on every keystroke.
const toEditable = (applied) => ({
  skills: (applied.skills || []).join(', '),
  location: applied.location || '',
  max_salary: applied.max_salary ?? '',
  min_experience: applied.min_experience ?? '',
  max_experience: applied.max_experience ?? '',
  gender: applied.gender || '',
  education: applied.education || '',
  languages: (applied.languages || []).join(', '),
});

// Candidate card matching the apnaHire "Database Matches" layout: a select
// checkbox, name + "View Full Profile" link, an icon meta row (experience /
// salary / location), highlighted Current/Latest role, Education, Pref.
// Location, Skills (matched ones highlighted, capped with a "+N more"
// chip), Languages, phone number, and a footer strip with CV status and
// last-active date. All candidate data is shown unlocked — no gating.
function CandidateCard({ candidate, selected, onToggleSelect }) {
  const [skillsExpanded, setSkillsExpanded] = useState(false);

  const experience = formatExperience(candidate.experience_years);
  const salary = formatSalary(candidate.expected_salary);
  const activeDate = formatActiveDate(candidate.last_active_at);
  // Field name for phone varies across APIs — check the common variants so
  // the button shows up regardless of which one your backend actually sends.
  const [unlockedContact, setUnlockedContact] = useState(null);
  const phoneNumber = unlockedContact ? unlockedContact.phone : (candidate.phone_number || candidate.mobile_number || candidate.mobile || candidate.contact_number || candidate.phone);
  const contactLocked = !!candidate.contact_locked && !unlockedContact;
  const resumeUrl = (unlockedContact && unlockedContact.resume_url) || candidate.resume_url;

  const visibleSkills = skillsExpanded ? candidate.skills : (candidate.skills || []).slice(0, 4);
  const hiddenSkillCount = (candidate.skills?.length || 0) - visibleSkills.length;

  return (
    <div className="bg-white rounded-xl shadow p-5">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={!!selected}
          onChange={() => onToggleSelect(candidate.id)}
          className="mt-1.5 w-4 h-4 accent-[#2563EB] shrink-0"
        />

        <div className="w-11 h-11 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center shrink-0 overflow-hidden">
  {candidate.profile_photo_url ? (
    <img src={candidate.profile_photo_url} alt={candidate.name} className="w-full h-full object-cover" />
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" />
    </svg>
  )}
</div>

       <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h3 className="font-bold text-[#0D1B3D]">{candidate.name}</h3>
              <a
                href={`/employer/candidates/${candidate.id}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold text-[#10B981] hover:underline whitespace-nowrap"
              >
                View Full Profile »
              </a>
            </div>
            <MatchBadge percent={candidate.match_percentage} />
          </div>

         <MatchBreakdown breakdown={candidate.match_breakdown} />

          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            {experience && (
              <span className="inline-flex items-center gap-1"><BriefcaseIcon /> {experience}</span>
            )}
            {salary && <span className="inline-flex items-center gap-1">💰 {salary}</span>}
            {candidate.city && (
              <span className="inline-flex items-center gap-1"><PinIcon /> {candidate.city}</span>
            )}
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm">
            {(candidate.current_designation || candidate.current_company) && (
              <>
                <span className="text-gray-400 shrink-0">Current / Latest</span>
                <span className="font-medium text-[#0D1B3D] bg-yellow-100/70 px-1.5 py-0.5 rounded w-fit">
                  {candidate.current_designation}
                  {candidate.current_designation && candidate.current_company ? ' at ' : ''}
                  {candidate.current_company}
                </span>
              </>
            )}

            {candidate.educations?.length > 0 && (
              <>
                <span className="text-gray-400 shrink-0">Education</span>
                <span className="text-gray-700">
                  {candidate.educations.map((e) => e.degree).filter(Boolean).join(', ')}
                </span>
              </>
            )}

            {candidate.preferred_location && (
              <>
                <span className="text-gray-400 shrink-0">Pref. Location</span>
                <span className="text-gray-700">{candidate.preferred_location}</span>
              </>
            )}

            {candidate.skills?.length > 0 && (
              <>
                <span className="text-gray-400 shrink-0">Skills</span>
                <span className="flex flex-wrap items-center gap-1.5">
                  {visibleSkills.map((s) => (
                    <span
                      key={s.name}
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        s.matched ? 'bg-yellow-100 text-yellow-800 font-semibold' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {s.name}
                    </span>
                  ))}
                  {hiddenSkillCount > 0 && (
                    <button
                      onClick={() => setSkillsExpanded(true)}
                      className="text-xs font-semibold text-[#2563EB] hover:underline"
                    >
                      +{hiddenSkillCount} more
                    </button>
                  )}
                </span>
              </>
            )}

            {candidate.languages?.length > 0 && (
              <>
                <span className="text-gray-400 shrink-0">Languages</span>
                <span className="text-gray-700">
                  {candidate.languages.map((l) => `${l.name}${l.proficiency ? ` (${l.proficiency})` : ''}`).join(' | ')}
                </span>
              </>
            )}
          </div>

          {(phoneNumber || contactLocked || resumeUrl) && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {contactLocked && (
                <UnlockContact candidateId={candidate.id} onUnlocked={setUnlockedContact} />
              )}
              {phoneNumber && (
                <a
                  href={`tel:${phoneNumber}`}
                  className="inline-flex items-center gap-1.5 bg-[#0F8B6D] hover:bg-[#0d755b] text-white text-sm font-semibold px-4 py-2 rounded-lg"
                >
                  <PhoneIcon /> {phoneNumber}
                </a>
              )}
              {resumeUrl && (
                <a
                  href={resumeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 bg-white border border-[#2563EB] text-[#2563EB] hover:bg-[#EFF6FF] text-sm font-semibold px-4 py-2 rounded-lg"
                >
                  <ResumeIcon /> View Resume
                </a>
              )}
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
            {activeDate && <span>Active on {activeDate}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EmployerCandidateDatabase() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 20 });
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [editableFilters, setEditableFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchMatches = useCallback(async (page, filtersToSend) => {
    setLoading(true);
    setError('');
    try {
      const params = { page, per_page: 20 };
      if (filtersToSend) {
        params.skills = filtersToSend.skills;
        params.location = filtersToSend.location;
        params.max_salary = filtersToSend.max_salary;
        params.min_experience = filtersToSend.min_experience;
        params.max_experience = filtersToSend.max_experience;
        params.gender = filtersToSend.gender;
        params.education = filtersToSend.education;
        params.languages = filtersToSend.languages;
      }
      const res = await api.get(`/jobs/${jobId}/database-matches`, { params });
      setJob(res.data.job);
      setAppliedFilters(res.data.applied_filters);
      setEditableFilters(toEditable(res.data.applied_filters));
      setCandidates(res.data.data);
      setSelectedIds([]);
      setMeta({
        current_page: res.data.current_page,
        last_page: res.data.last_page,
        total: res.data.total,
        per_page: res.data.per_page,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't load candidate matches.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    // First load: send no filter params at all, so the backend applies
    // its job-derived defaults. Every load after this sends the full
    // current filter state explicitly (see applyFilters / removeFilter).
    fetchMatches(1, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const applyFilters = () => {
    const payload = {
      skills: editableFilters.skills.split(',').map((s) => s.trim()).filter(Boolean).join(','),
      location: editableFilters.location.trim(),
      max_salary: editableFilters.max_salary,
      min_experience: editableFilters.min_experience,
      max_experience: editableFilters.max_experience,
      gender: editableFilters.gender,
      education: editableFilters.education.trim(),
      languages: editableFilters.languages.split(',').map((s) => s.trim()).filter(Boolean).join(','),
    };
    fetchMatches(1, payload);
  };

  const removeFilterChip = (key) => {
    const cleared = { ...toEditable(appliedFilters), [key]: '' };
    setEditableFilters(cleared);
    const payload = {
      skills: key === 'skills' ? '' : (appliedFilters.skills || []).join(','),
      location: key === 'location' ? '' : (appliedFilters.location || ''),
      max_salary: key === 'max_salary' ? '' : (appliedFilters.max_salary ?? ''),
      min_experience: key === 'min_experience' ? '' : (appliedFilters.min_experience ?? ''),
      max_experience: key === 'max_experience' ? '' : (appliedFilters.max_experience ?? ''),
      gender: key === 'gender' ? '' : (appliedFilters.gender || ''),
      education: key === 'education' ? '' : (appliedFilters.education || ''),
      languages: key === 'languages' ? '' : (appliedFilters.languages || []).join(','),
    };
    fetchMatches(1, payload);
  };

  const resetToJobDefaults = () => {
    fetchMatches(1, null);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.length === candidates.length ? [] : candidates.map((c) => c.id)));
  };

  const handleDownloadExcel = async () => {
    try {
      const res = await api.get(`/jobs/${jobId}/database-matches/export`, {
        params: { ids: selectedIds.join(',') || undefined },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `candidates-job-${jobId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to export candidates.');
    }
  };

  const filterChips = [];
  if (appliedFilters) {
    if (appliedFilters.skills?.length) filterChips.push({ key: 'skills', label: appliedFilters.skills.join(', ') });
    if (appliedFilters.location) filterChips.push({ key: 'location', label: appliedFilters.location });
    if (appliedFilters.max_salary) filterChips.push({ key: 'max_salary', label: `≤ ${formatSalary(appliedFilters.max_salary) || appliedFilters.max_salary}` });
    if (appliedFilters.min_experience) filterChips.push({ key: 'min_experience', label: `${appliedFilters.min_experience}+ yrs` });
    if (appliedFilters.max_experience) filterChips.push({ key: 'max_experience', label: `≤ ${appliedFilters.max_experience} yrs` });
    if (appliedFilters.gender) filterChips.push({ key: 'gender', label: appliedFilters.gender });
    if (appliedFilters.education) filterChips.push({ key: 'education', label: appliedFilters.education });
    if (appliedFilters.languages?.length) filterChips.push({ key: 'languages', label: appliedFilters.languages.join(', ') });
  }

  const allSelected = candidates.length > 0 && selectedIds.length === candidates.length;

 return (
  <div className="min-h-screen bg-[#EFF6FF]">
    <Navbar user={user} />
    <EmployerSidebar user={user} activeTab="database" />

    <div className="lg:pl-16 lg:peer-hover:pl-64 transition-all duration-200 ease-in-out">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <EmployerSidebar user={user} activeTab="database" mobile />
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/employer/jobs')} className="text-gray-500 hover:text-gray-700">
            <ChevronLeftIcon />
          </button>
          <h1 className="text-2xl font-bold text-[#0D1B3D]">
            {job?.title || 'Job'} <span className="text-gray-400 font-normal">— Database Matches</span>
          </h1>
        </div>

        {error && (
          <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
            <span>{error}</span>
            <button onClick={() => fetchMatches(meta.current_page, appliedFilters)} className="ml-3 font-medium underline hover:no-underline">
              Retry
            </button>
          </div>
        )}

        <button
          onClick={() => setShowFiltersMobile((s) => !s)}
          className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#0D1B3D]"
        >
          <span className="inline-flex items-center gap-2"><FunnelIcon /> Filters</span>
          <span className="text-sm text-gray-400">{showFiltersMobile ? 'Hide ▲' : 'Show ▼'}</span>
        </button>

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* Filters sidebar */}
          <div className={`${showFiltersMobile ? 'block' : 'hidden'} lg:block w-full lg:w-72 shrink-0 space-y-4 lg:sticky lg:top-8`}>
            <div className="bg-white rounded-xl shadow p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-[#0D1B3D] text-sm inline-flex items-center gap-1.5">
                  <FunnelIcon /> Filters
                </h3>
                <button onClick={resetToJobDefaults} className="text-xs font-semibold text-[#2563EB] hover:underline">
                  Reset to job defaults
                </button>
              </div>

              {filterChips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {filterChips.map((chip) => (
                    <span key={chip.key} className="inline-flex items-center gap-1 bg-[#EFF6FF] text-[#2563EB] text-xs font-medium px-2 py-1 rounded-full">
                      {chip.label}
                      <button onClick={() => removeFilterChip(chip.key)} className="hover:text-red-500">
                        <XIcon />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Skills (comma separated)</label>
                  <input
                    type="text"
                    value={editableFilters.skills}
                    onChange={(e) => setEditableFilters({ ...editableFilters, skills: e.target.value })}
                    placeholder="e.g. PHP, Laravel"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Location / City</label>
                  <input
                    type="text"
                    value={editableFilters.location}
                    onChange={(e) => setEditableFilters({ ...editableFilters, location: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Max expected salary (₹/yr)</label>
                  <input
                    type="number"
                    value={editableFilters.max_salary}
                    onChange={(e) => setEditableFilters({ ...editableFilters, max_salary: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Min exp. (yrs)</label>
                    <input
                      type="number"
                      value={editableFilters.min_experience}
                      onChange={(e) => setEditableFilters({ ...editableFilters, min_experience: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Max exp. (yrs)</label>
                    <input
                      type="number"
                      value={editableFilters.max_experience}
                      onChange={(e) => setEditableFilters({ ...editableFilters, max_experience: e.target.value })}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Gender</label>
                  <select
                    value={editableFilters.gender}
                    onChange={(e) => setEditableFilters({ ...editableFilters, gender: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Any</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Education (e.g. 10th, 12th, B.Com)</label>
                  <input
                    type="text"
                    value={editableFilters.education}
                    onChange={(e) => setEditableFilters({ ...editableFilters, education: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Languages (comma separated)</label>
                  <input
                    type="text"
                    value={editableFilters.languages}
                    onChange={(e) => setEditableFilters({ ...editableFilters, languages: e.target.value })}
                    placeholder="e.g. Kannada, Malayalam"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <button
                  onClick={applyFilters}
                  className="w-full bg-[#0F8B6D] hover:bg-[#0d755b] text-white font-semibold px-4 py-2 rounded-lg text-sm"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>

          {/* Candidate list */}
          <div className="flex-1 w-full space-y-4">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{meta.total} candidate{meta.total === 1 ? '' : 's'} found</span>
              {meta.last_page > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    disabled={meta.current_page <= 1 || loading}
                    onClick={() => fetchMatches(meta.current_page - 1, appliedFilters)}
                    className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 disabled:opacity-40 hover:bg-white"
                  >
                    <ChevronLeftIcon />
                  </button>
                  <span>Page {meta.current_page} of {meta.last_page}</span>
                  <button
                    disabled={meta.current_page >= meta.last_page || loading}
                    onClick={() => fetchMatches(meta.current_page + 1, appliedFilters)}
                    className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 disabled:opacity-40 hover:bg-white"
                  >
                    <ChevronRightIcon />
                  </button>
                </div>
              )}
            </div>

            {/* Select All / Download Excel toolbar */}
            {candidates.length > 0 && (
              <div className="bg-white rounded-xl shadow p-4 flex items-center justify-between">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-[#2563EB]"
                  />
                  Select All
                  {selectedIds.length > 0 && (
                    <span className="text-gray-400 font-normal">({selectedIds.length} selected)</span>
                  )}
                </label>

                <button
                  onClick={handleDownloadExcel}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F8B6D] hover:text-[#0d755b]"
                >
                  <DownloadIcon /> Download Excel
                </button>
              </div>
            )}

            {loading ? (
              <p className="text-gray-500">Loading candidates...</p>
            ) : candidates.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-10 text-center text-gray-500">
                No candidates match these filters. Try widening them.
              </div>
            ) : (
              candidates.map((c) => (
                <CandidateCard
                  key={c.id}
                  candidate={c}
                  selected={selectedIds.includes(c.id)}
                  onToggleSelect={toggleSelect}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}